import { minuteMs } from "verdi-common/timeConstants";
import { MasterIndex } from "verdi-web-client";
import { SessionHandler } from "verdi-web-client/APICommands/SessionHandler";

import { type PollForUpdatesResponse } from "./types";

/**
 * Variable to store the most recent poll time
 */
let mostRecentPollTime = new Date();
SessionHandler.onSessionLoad.addListener(() => {
    mostRecentPollTime = new Date();
});

interface RefreshChangedDevicesParams {
    includeRecentData?: boolean;
    includeUpdatedDevices?: boolean;
    includeSpecificDeviceIDs?: string[];
}

/**
 * Data is timestamped at the time of reading, not when data is updated in the table.
 * This means that data that takes longer than a polling interval to go from measurement
 * to being updated in the table will be skipped as it will be older than the last poll time.
 *
 * We add a buffer of 3 uplink cycles (2 minute cycle on gen3 and later devices) to ensure no data is missed.
 */
const LAST_POLL_TIME_BUFFER_MS = 6 * minuteMs;

/**
 * Refresh all the devices that have changed since the last poll
 *
 * @param includeRecentData - Include most recent data points
 * @param includeUpdatedDevices - Include key fields from devices that have been updated since the last poll
 * @param includeSpecificDeviceIDs - node_ids to force to be included
 * @return {Promise<void>}
 */
export const refreshChangedDevices = async ({
    includeRecentData = true,
    includeUpdatedDevices = true,
    includeSpecificDeviceIDs = undefined,
}: RefreshChangedDevicesParams) => {
    try {
        if (SessionHandler.curUser) {
            const newData = await queryChangedData({
                lastPollTime: new Date(mostRecentPollTime.getTime() - LAST_POLL_TIME_BUFFER_MS),
                includeRecentData,
                includeUpdatedDevices,
                deviceIds: includeSpecificDeviceIDs ?? [],
            });

            await updateMasterIndexFromJson(newData);
            mostRecentPollTime = new Date(newData?.timeOfPoll ?? new Date());
        }
    } catch (error) {
        console.error("Error polling devices", error);
    }
};

interface QueryChangedDataParams {
    lastPollTime: Date;
    includeRecentData: boolean;
    includeUpdatedDevices: boolean;
    deviceIds: string[];
}
/**
 * Query the for changed data from the server
 */
const queryChangedData = ({
    lastPollTime,
    includeRecentData,
    includeUpdatedDevices,
    deviceIds,
}: QueryChangedDataParams) =>
    SessionHandler.doFetch(`pollForUpdates`, {
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            compress: "true",
        },
        method: "POST",
        redirect: "follow",
        body: JSON.stringify({
            includeRecentData,
            includeUpdatedDevices,
            lastPollTime,
            includeSpecificDevices: deviceIds,
        }),
    }).then((response) => response.json());

/**
 * Update the master index data structure from the raw json data
 */
const updateMasterIndexFromJson = (json: PollForUpdatesResponse) => {
    if (json?.deviceUpdates) {
        json.deviceUpdates.forEach((deviceUpdate: any) => {
            const deviceObject =
                MasterIndex.irrigationDevice.byID[deviceUpdate.node_id] ??
                MasterIndex.blockValve.byID[deviceUpdate.node_id];
            if (!deviceObject) {
                return;
            }
            deviceObject?.assignFromJSON(deviceUpdate);
            deviceObject.lastRefreshed = new Date();
            deviceObject.onChange.trigger({});
        });
    }

    // Add in recentData for all devices
    if (json?.recentData) {
        // recentData is always array of length 1
        const updatedDeviceIds = updateRecentData(json.recentData[0]);

        // Trigger the legacy event listener
        SessionHandler.onRecentDataUpdated.trigger({
            affectedDevices: updatedDeviceIds,
        });
    }
    if (json?.thirdPartyDeviceUpdates) {
        json.thirdPartyDeviceUpdates.forEach((deviceUpdate: any) => {
            const deviceObject = MasterIndex.thirdPartyDevice.byID[deviceUpdate.node_id];
            if (!deviceObject) {
                return;
            }
            deviceObject?.assignFromJSON(deviceUpdate);
            deviceObject.lastRefreshed = new Date();
            deviceObject.onChange.trigger({});
        });
    }
};

/**
 * Iterates over the recent data and updates all recentData items that have newer data
 */
const updateRecentData = (recentData: PollForUpdatesResponse["recentData"][0]) => {
    const idsUpdated = new Set<string>();

    Object.keys(recentData.data).forEach((recentDataKey: string) => {
        const [dataKey, deviceId] = recentDataKey.split("__");
        const deviceObject =
            MasterIndex.blockValve.byID[deviceId] ??
            MasterIndex.irrigationDevice.byID[deviceId] ??
            MasterIndex.thirdPartyDevice.byID[deviceId] ??
            MasterIndex.SensoterraMoisture.byID[deviceId];

        if (!deviceObject) {
            return;
        }
        // @ts-ignore recentData[dataKey] does exist on the device object but isnt typed properly
        const existingDataPoint = deviceObject.recentData[dataKey];

        const newDataPointDate = new Date(recentData.data[recentDataKey].date);
        const existingDataPointDate = new Date(existingDataPoint?.date);

        if (!existingDataPoint || newDataPointDate.valueOf() > existingDataPointDate.valueOf()) {
            // @ts-ignore recentData[dataKey] does exist on the device object but isnt typed properly
            deviceObject.recentData[dataKey] = {
                value: recentData.data[recentDataKey].value,
                date: new Date(recentData.data[recentDataKey].date),
            };
            idsUpdated.add(deviceId);
        }
    });
    return idsUpdated;
};
